import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import { helpPath, helpSlugFromHref } from '../../lib/help'

/**
 * GitHub's sanitize schema, plus the `help:` protocol on links. No images, no
 * raw HTML: the article subset has neither (help-api-contract.md § Markdown).
 */
export const helpSanitizeSchema = {
  ...defaultSchema,
  tagNames: (defaultSchema.tagNames ?? []).filter((tag) => tag !== 'img'),
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), 'help'],
  },
}

/** react-markdown's own transform drops unknown protocols; `help:` is ours. */
export function helpUrlTransform(url: string): string {
  return helpSlugFromHref(url) ? url : defaultUrlTransform(url)
}

const linkClass = 'font-medium text-primary underline-offset-2 hover:underline'

interface HelpMarkdownProps {
  markdown: string
  /**
   * A `help:` link opens here instead of navigating (the "?" drawer). Left out,
   * it is a router link to /help/<slug>.
   */
  onHelpLink?: (slug: string) => void
  className?: string
}

function HelpLink({
  href,
  children,
  onHelpLink,
}: {
  href?: string
  children?: ReactNode
  onHelpLink?: (slug: string) => void
}) {
  const slug = helpSlugFromHref(href)
  if (slug) {
    if (onHelpLink) {
      return (
        <a
          href={helpPath(slug)}
          className={linkClass}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return
            e.preventDefault()
            onHelpLink(slug)
          }}
        >
          {children}
        </a>
      )
    }
    return (
      <Link to={helpPath(slug)} className={linkClass}>
        {children}
      </Link>
    )
  }
  if (!href) return <span>{children}</span>
  if (/^mailto:/i.test(href)) {
    return (
      <a href={href} className={linkClass}>
        {children}
      </a>
    )
  }
  if (/^https?:/i.test(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={`${linkClass} break-words`}>
        {children}
      </a>
    )
  }
  // A same-page anchor or anything else the sanitizer let through: plain text.
  return <span>{children}</span>
}

function components(onHelpLink?: (slug: string) => void): Components {
  return {
    h1: ({ children }) => (
      <h2 className="mt-6 mb-3 font-display text-lg font-bold text-foreground first:mt-0">{children}</h2>
    ),
    h2: ({ children }) => (
      <h2 className="mt-6 mb-2 font-display text-base font-bold text-foreground first:mt-0">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-5 mb-2 text-sm font-semibold text-foreground first:mt-0">{children}</h3>
    ),
    h4: ({ children }) => <h4 className="mt-4 mb-1 text-sm font-semibold text-foreground">{children}</h4>,
    p: ({ children }) => <p className="mb-3 leading-relaxed last:mb-0">{children}</p>,
    ul: ({ children }) => <ul className="mb-3 list-disc space-y-1 pl-5 last:mb-0 [&_ul]:mt-1 [&_ul]:mb-0">{children}</ul>,
    ol: ({ children }) => (
      <ol className="mb-3 list-decimal space-y-1 pl-5 last:mb-0 [&_ol]:mt-1 [&_ol]:mb-0 [&_ul]:mt-1 [&_ul]:mb-0">
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    hr: () => <hr className="my-5 border-border" />,
    a: ({ href, children }) => (
      <HelpLink href={href} onHelpLink={onHelpLink}>
        {children}
      </HelpLink>
    ),
    code: ({ children }) => (
      <code className="rounded bg-surface-raised px-1 py-0.5 font-mono text-xs text-foreground">{children}</code>
    ),
    pre: ({ children }) => (
      <pre className="mb-3 overflow-x-auto rounded-lg border border-border bg-surface-raised p-3 last:mb-0">{children}</pre>
    ),
    blockquote: ({ children }) => (
      <blockquote className="mb-3 border-l-2 border-border pl-3 text-foreground-muted last:mb-0">{children}</blockquote>
    ),
    table: ({ children }) => (
      <div className="mb-3 overflow-x-auto rounded-xl border border-border last:mb-0" data-testid="help-table">
        <table className="min-w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-surface-raised">{children}</thead>,
    th: ({ children }) => (
      <th className="border-b border-border px-3 py-2 text-left text-xs font-semibold text-foreground">{children}</th>
    ),
    td: ({ children }) => (
      <td className="border-b border-border/60 px-3 py-2 align-top text-foreground-muted">{children}</td>
    ),
  }
}

/**
 * One help article's body: the README's markdown subset, sanitized. Used by the
 * article page and by the "?" drawer, so both read the same.
 */
export default function HelpMarkdown({ markdown, onHelpLink, className = '' }: HelpMarkdownProps) {
  return (
    <div className={`help-markdown text-sm text-foreground/90 ${className}`} data-testid="help-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, helpSanitizeSchema]]}
        urlTransform={helpUrlTransform}
        skipHtml
        components={components(onHelpLink)}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  )
}
